"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  type TooltipContentProps,
} from "recharts";
import type { MonthlyStats } from "@/lib/conditions/historical-stats";

type Props = {
  stats: MonthlyStats[];
  currentMonth: number; // 1-12
};

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function offshoreColor(pct: number): string {
  if (pct >= 50) return "#22C55E";
  if (pct >= 35) return "#84CC16";
  if (pct >= 20) return "#F59E0B";
  return "#EF4444";
}

function renderTooltip(props: TooltipContentProps) {
  const { active, payload } = props;
  if (!active || !payload || payload.length === 0) return null;
  const stat = payload[0].payload as MonthlyStats;

  return (
    <div className="bg-white rounded-lg border border-border-light shadow-lg p-3 text-[12px] min-w-[160px]">
      <div className="font-semibold text-navy mb-1.5">
        {MONTH_FULL[stat.month - 1]}
      </div>
      <div className="space-y-1 text-text-muted">
        <div>
          <span className="text-navy font-medium">Avg swell:</span>{" "}
          {stat.avgSwellHeightM.toFixed(1)}m / {stat.avgSwellPeriodS.toFixed(0)}s
        </div>
        <div>
          <span className="text-navy font-medium">Offshore days:</span>{" "}
          {Math.round(stat.offshorePct)}%
        </div>
        <div>
          <span className="text-navy font-medium">Air temp:</span>{" "}
          {Math.round(stat.avgAirTempC)}&deg;C
        </div>
        {stat.avgWaterTempC !== null && (
          <div>
            <span className="text-navy font-medium">Water temp:</span>{" "}
            {Math.round(stat.avgWaterTempC)}&deg;C
          </div>
        )}
        <div>
          <span className="text-navy font-medium">Rainfall:</span>{" "}
          {Math.round(stat.avgRainfallMm)} mm
        </div>
      </div>
    </div>
  );
}

export function HistoricalStats({ stats, currentMonth }: Props) {
  if (stats.length === 0) return null;

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <h3 className="font-serif text-[18px] md:text-[22px] font-bold text-navy mb-4">
        Historical averages
      </h3>

      <div className="h-[200px] md:h-[240px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 720, height: 200 }}
        >
          <BarChart
            data={stats}
            margin={{ top: 4, right: 4, bottom: 4, left: 0 }}
          >
            <XAxis
              dataKey="month"
              tickFormatter={(v: number) => MONTH_LABELS[v - 1] ?? ""}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(v: number) => `${v}m`}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickLine={false}
              axisLine={false}
              width={35}
            />
            <Tooltip content={renderTooltip} />
            <Bar
              dataKey="avgSwellHeightM"
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            >
              {stats.map((entry) => (
                <Cell
                  key={entry.month}
                  fill={offshoreColor(entry.offshorePct)}
                  opacity={entry.month === currentMonth ? 1 : 0.65}
                  stroke={entry.month === currentMonth ? "#1B2840" : "none"}
                  strokeWidth={entry.month === currentMonth ? 2 : 0}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-3 text-[11px] text-text-muted flex-wrap">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: "#22C55E" }}
          />
          50%+ offshore
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: "#F59E0B" }}
          />
          20-49% offshore
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: "#EF4444" }}
          />
          Below 20%
        </span>
      </div>
    </div>
  );
}
